import useUnifiedCardContainer, {
  RawCardContainer,
  UnifiedCardContainerOpts,
  useSidebarWidth,
} from "./useUnifiedCardContainer";
import {Col, DSIconBookmark, DSIconButton, DSIconDoc, DSIconEye, DSSpinner, Text} from "@cdx/ds";
import useBling from "../../lib/hooks/useBling";
import {
  AttachmentPanelButton,
  ChildCardPanelButtonForCardCreation,
  PropPanelButton,
} from "./SidePanelButtons";
import {AddAnotherItem, openSidePanel} from "./card-container-utils";
import PropEdit from "../../components/PropEdit";
import {dayToDateStr, toCmpDate} from "../../lib/date-utils";
import {api} from "../../lib/api";
import RawAttachments from "./sidebars/attachments";
import Uploader, {useUpload} from "../../components/uploader";
import {useEffect, useRef, useState} from "react";
import CardDetailFrame, {CardDetailFrameAboveBottom} from "./CardDetailFrame";
import {TooltipForChild, cx, useGlobalKeyPress} from "@cdx/common";
import dsStyles from "@cdx/ds/css/index.css";
import {cardContainerStyles} from "./unified-card-container.css";
import {createPersistStore} from "../../lib/hooks/usePersistStore";
import useStateIfLoaded from "../../lib/hooks/useStateIfLoaded";
import {canAddHandToQueueErrors, getFreeHandSlotsForUser} from "../hand-queue/hand-queue-utils";
import {waitForResultPromise} from "../../lib/wait-for-result";
import {cardHighlightActions} from "../card-highlight/useCardHighlight";
import {getImageDimensionsFromUrl} from "../../components/CdxImg";
import FilePasteHandler from "./FilePasteHandler";
import {useCreationHint} from "./useCreationHint";
import {deckHasDefaultCard} from "../workflows/workflow-utils";
import confirm from "../../lib/confirm";
import {joinAnd} from "../../lib/utils";
import {CreateCardTopArea} from "./CreateCardTopArea";
import {CardContentForm} from "./CardContentForm";
import {PreviewCardContent} from "./PreviewCardContent";
import {checkIfPresent} from "../../lib/mate/mate-utils";
import useFocusMode, {FocusProps} from "./useFocusMode";
import {usePropOnHoverChange} from "../../components/Card/useHoverShortcuts";
import {
  lexicalSerializer,
  useCdxEditorState,
} from "../../components/RichTextarea/Lexical/LexicalRichTextProvider";
import {ArenaCtx} from "../card-panel/card-panel-utils";
import WorkflowSidebarForCardCreation from "../workflows/WorkflowSidebarForCardCreation";
import {Root} from "../../cdx-models/Root";
import {Deck, DeckId} from "../../cdx-models/Deck";
import {CardId} from "../../cdx-models/Card";

const Attachments = RawAttachments as any;
const ShowUploads = Uploader.Uploads as any;

const category = "Card creation";

const propToLabel = {
  effort: "effort",
  priority: "priority",
  assignee: "owner",
  masterTags: "project tags",
};

const BookmarkBottomButton = ({onChange, card}: {onChange: (opts: any) => void; card: any}) => {
  const getTooltip = () => {
    if (!card.deck) return "Cards without deck are automatically bookmarked";
    if (card.addAsBookmark) return "Card will be bookmarked";
    return "Click to bookmark card";
  };

  const handleClick = () => onChange({asBookmark: !card.asBookmark});

  useGlobalKeyPress({
    key: ".",
    fn: handleClick,
    description: "Bookmark card",
    category,
  });

  const disabled = !card.deck;

  return (
    <TooltipForChild tooltip={getTooltip()} delayed targetIsDisabled={disabled} placement="right">
      <DSIconButton
        variant="tertiary"
        size="sm"
        active={card.asBookmark || !card.deck}
        icon={<DSIconBookmark />}
        onClick={handleClick}
        disabled={disabled}
      />
    </TooltipForChild>
  );
};

const DocCardBottomButton = ({onChange, card}: {onChange: (opts: any) => void; card: any}) => {
  const allowedCardTypes = card.deck?.allowedCardTypes ?? ["doc"];
  const allowedDocCards = allowedCardTypes.includes("doc");
  const withinDocDeck = isDocDeck(card.deck);

  const getDisabledReason = () => {
    if (card.isDoc) {
      if (withinDocDeck) return "The target Deck only allows Doc Cards";
    } else {
      if (!allowedDocCards) return "The target Deck does not allow Doc Cards";
    }
  };

  const disabledReason = getDisabledReason();

  const handleClick = () => onChange({isDoc: !card.isDoc});
  return (
    <TooltipForChild
      tooltip={disabledReason || (card.isDoc ? "Turn into Task Card" : "Turn into Doc Card")}
      delayed
      placement="right"
      targetIsDisabled={Boolean(disabledReason)}
    >
      <DSIconButton
        variant="tertiary"
        size="sm"
        active={card.isDoc}
        icon={<DSIconDoc />}
        onClick={handleClick}
        disabled={Boolean(disabledReason)}
      />
    </TooltipForChild>
  );
};

const SubscribeBottomButton = ({onChange, card}: {onChange: (opts: any) => void; card: any}) => {
  const handleClick = () => onChange({subscribeCreator: !card.subscribeCreator});
  const tooltip = card.subscribeCreator
    ? "You will be watching this card: you'll receive card notifications when the card gets started and completed or when a conversation is started."
    : "You only receive card notifications when you are part of a conversation or when you become the card owner.";
  return (
    <TooltipForChild tooltip={tooltip} delayed placement="right">
      <DSIconButton
        variant="tertiary"
        size="sm"
        active={card.subscribeCreator}
        icon={<DSIconEye />}
        onClick={handleClick}
      />
    </TooltipForChild>
  );
};

const BottomButtons = (props: {card: any; onChange: (c: any) => void}) => {
  const {card, onChange} = props;

  return (
    <Col mt="auto" sp="4px">
      <BookmarkBottomButton onChange={onChange} card={card} />
      <DocCardBottomButton card={card} onChange={onChange} />
      <SubscribeBottomButton card={card} onChange={onChange} />
    </Col>
  );
};

const dataAttachmentToFakeInstanceAttachment = (attachment: any) => {
  const {fileData, creatorId, ...rest} = attachment;
  const typeMatch = (fileData.type || "").match(/image\/(.*)/);
  const file = {
    id: attachment.id,
    size: fileData.size,
    url: fileData.url,
    meta: typeMatch ? {type: typeMatch[1]} : {},
    name: fileData.fileName,
    $meta: {
      get(field: "id" | "size" | "url" | "meta" | "name") {
        return file[field];
      },
      isFieldLoaded() {
        return true;
      },
      isFieldPresent() {
        return true;
      },
    },
  };
  return {
    ...rest,
    creator: api.getModel({modelName: "user", id: creatorId}),
    file,
    title: fileData.fileName,
    createdAt: new Date(),
  };
};

let nextFakeAttachmentId = 1;
let nextId = 1;

export const cardCreationPersistStore = createPersistStore({
  elementToKey: (arenaCtx: any) => {
    const id = arenaCtx.model?.$meta.get("id", false);
    return id !== false ? `new-card-${arenaCtx.type}-${id || ""}` : "";
  },
});

const arraysWithDifferentContent = (arr1: any[], arr2: any[]) => {
  if (arr1.length !== arr2.length) return true;
  const s1 = new Set(arr1);
  return !arr2.every((el) => s1.has(el));
};

const applyDeckChange = ({data, deckId, contentField, isEmpty}: any) => {
  if (deckId === null) return Promise.resolve({});
  return waitForResultPromise(() => {
    const deck = api.getModel({modelName: "deck", id: deckId})!;
    const getDefaultCardData = () => {
      if (!deckHasDefaultCard(deck)) return null;
      return {
        effort: null,
        priority: null,
        assigneeId: null,
        masterTags: [],
        content: "",
        ...deck.defaultCard,
      };
    };
    return {
      defaultCardData: getDefaultCardData(),
      allowedCardTypes: deck.allowedCardTypes,
      sprintId: getSprint(deckId, api.getRoot()),
    };
  }).then(({defaultCardData, allowedCardTypes, sprintId}) => {
    let updates: any = {};
    if (allowedCardTypes.length === 1 && allowedCardTypes[0] === "doc") {
      updates.isDoc = true;
    } else if (!allowedCardTypes.includes("doc")) {
      updates.isDoc = false;
    }
    if (sprintId !== null && !data.sprintId) {
      updates.sprintId = sprintId;
    }

    if (defaultCardData === null) return updates;
    const {effort, priority, assigneeId, masterTags, content: defaultCardContent} = defaultCardData;
    if (defaultCardContent) {
      if (isEmpty) {
        contentField.setText(defaultCardContent);
      } else {
        if (contentField.getText().trim() !== defaultCardContent.trim()) {
          contentField.addText(`\n\n${defaultCardContent}`);
        }
      }
    }

    const overwrittenProps = {} as any;
    if (effort !== null && effort !== data.effort) {
      if (data.effort !== null) {
        overwrittenProps.effort = effort;
      } else {
        updates = {...updates, effort};
      }
    }
    if (priority !== null && priority !== data.priority) {
      if (data.priority !== null) {
        overwrittenProps.priority = priority;
      } else {
        updates = {...updates, priority};
      }
    }
    if (assigneeId !== null && assigneeId !== data.assignee) {
      if (data.assignee !== null) {
        overwrittenProps.assignee = assigneeId;
      } else {
        updates = {...updates, assignee: assigneeId};
      }
    }
    if (masterTags.length > 0 && arraysWithDifferentContent(data.masterTags, masterTags)) {
      if (data.masterTags.length > 0) {
        overwrittenProps.masterTags = masterTags;
      } else {
        updates = {...updates, masterTags};
      }
    }
    const propKeys = Object.keys(overwrittenProps) as (keyof typeof propToLabel)[];
    if (propKeys.length === 0) {
      return updates;
    } else {
      return confirm({
        title: "Apply this deck's template properties?",
        content: `This would overwrite these properties: ${joinAnd(
          propKeys.map((k) => propToLabel[k])
        )}`,
        confirmLabel: "Yes, overwrite these properties",
        cancelLabel: "No",
      }).then((ok: boolean) => (ok ? {...updates, ...overwrittenProps} : updates));
    }
  });
};

const useCardErrors = ({root, card, putInQueue}: {root: Root; card: any; putInQueue: boolean}) => {
  const me = root.loggedInUser;
  const errors = new Set<string>();
  if (!card.deck && card.assignee && card.assignee !== me) {
    errors.add("Please pick a deck. You can't add private cards for other users.");
  }
  if (putInQueue && card.assignee?.id) {
    const {isLoaded, result: reason} = checkIfPresent(
      () => canAddHandToQueueErrors(card, card.assignee.id, root),
      api
    );
    if (isLoaded && reason) {
      errors.add(`${reason}. Don't add it to the owner's hand via the hand button at the top.`);
    }
  }
  const projectId = card.deck?.project.$meta.get("id", null);
  if (card.milestone && projectId) {
    const sharedProject = card.milestone.$meta.exists("milestoneProjects", {projectId}, true);
    if (!sharedProject) {
      errors.add("This card's deck is not allowed for this milestone.");
    }
  }
  if (card.sprint && projectId) {
    const sharedProject = card.sprint.sprintConfig.$meta.exists(
      "sprintProjects",
      {projectId},
      true
    );
    if (!sharedProject) {
      errors.add("This card's deck is not allowed for this Run.");
    }
  }
  return errors;
};

const getContentKey = (arenaCtx: ArenaCtx) => {
  const id = arenaCtx.model ? arenaCtx.model.$meta.get("id", null) : "default";
  return id ? `${arenaCtx.type}-${id}` : null;
};

const isDocDeck = (deck: Deck | null) => {
  if (!deck) return false;
  if (deck.allowedCardTypes.length > 1) return false;
  return deck.allowedCardTypes[0] === "doc";
};

const getSprint = (deckId: DeckId | null, root: Root) => {
  if (!deckId) return null;
  const accountId = root.account.$meta.get("id", null);
  if (!accountId) return null;
  const autoAssignSprints = root.account.$meta.find("sprints", {
    isDeleted: false,
    completedAt: null,
    endDate: {op: "gte", value: toCmpDate(new Date())},
    sprintConfig: {
      autoAssignNewCard: true,
      sprintProjects: {
        project: {decks: {id: deckId}},
      },
    },
    $order: ["endDate"],
    $limit: 1,
  });
  return autoAssignSprints.length ? autoAssignSprints[0].id : null;
};

type InnerCreateCardProps = {
  root: Root;
  onClose: () => void;
  arenaCtx: ArenaCtx;
  goToCard: (opts: {cardId: CardId}) => void;
  deck: Deck | null;
  inboxDeck: Deck | null;
  initialSidePanel: string | null;
} & Pick<UnifiedCardContainerOpts<any>, "location" | "history">;
const InnerCreateCard = (props: InnerCreateCardProps) => {
  const {location, history, onClose, root, arenaCtx, deck, goToCard, inboxDeck} = props;
  const [attBlingRef, setAttBling] = useBling();
  const [showPreview, setShowPreview] = useState(false);
  const [createAnother, setCreateAnother] = useState(false);

  const togglePreview = () => setShowPreview((v) => !v);

  const handleCloseSidePanel = () => openSidePanel(history, location, null, true);

  const onMilestone = arenaCtx.type === "milestone";
  const onSprint = arenaCtx.type === "sprint";
  const sprint = onSprint ? arenaCtx.model : null;

  const getInitialMilestone = () => {
    if (onMilestone) return arenaCtx.model;
    if (deck) return deck.milestone;
    if (sprint) return sprint.autoMilestone;
    return null;
  };

  const milestone = getInitialMilestone();
  const inQueue = arenaCtx.type === "handQueue";

  const [isEmpty, setIsEmpty] = useState(true);

  const contentField = useCdxEditorState({
    initialContent: () =>
      lexicalSerializer.fromJSON(
        (cardCreationPersistStore.getStoredValue(arenaCtx) as any)?.content
      ) || (deck ? deck.$meta.get("defaultCard", {content: null}).content || "" : ""),
    contentKey: getContentKey(arenaCtx),
    onChangeListeners: {
      isEmpty: setIsEmpty,
    },
  });

  const targetProps = (location as any).state?.targetProps ?? null;

  const {state: nonContentData, setState: setNonContentData} = useStateIfLoaded(() => {
    const {content: _, ...rest} = (cardCreationPersistStore.getStoredValue(arenaCtx) || {}) as any;
    const {
      effort = null,
      priority = null,
      assigneeId = inQueue ? root.loggedInUser.id : null,
      masterTags = [],
    } = (deck ? deck.defaultCard : {}) as Record<string, any>;

    const deckId =
      targetProps?.deckId || rest?.deckId || (deck ? deck.$meta.get("id", null) : null);

    return {
      id: "new",
      deckId: deck ? deck.$meta.get("id", null) : null,
      milestoneId: milestone ? milestone.$meta.get("id", null) : null,
      sprintId: sprint ? sprint.$meta.get("id", null) : getSprint(deckId, root),
      masterTags,
      attachments: [],
      effort,
      priority,
      assignee: assigneeId,
      putInQueue: inQueue && assigneeId && getFreeHandSlotsForUser(assigneeId, root) > 0,
      coverFileId: null,
      childCards: [],
      inDeps: [],
      outDeps: [],
      presetId: null,
      subscribeCreator: false,
      asBookmark: false,
      isDoc: isDocDeck(deck),
      parentCardId: null,
      ...rest,
      ...targetProps,
    };
  });

  useEffect(() => {
    setNonContentData((prev: any) => ({...prev, ...targetProps}));
  }, [targetProps, setNonContentData]);

  const data = {content: contentField.state, ...(nonContentData as any)};
  const justSavedIdRef = useRef(null);
  cardCreationPersistStore.usePersistValues(arenaCtx, () => {
    if (data.id === justSavedIdRef.current) return;
    const defaultContent = deck?.$meta.get("defaultCard", {content: null}).content ?? "";
    if (!isEmpty && (!defaultContent || contentField.getText() !== defaultContent)) {
      return {content: lexicalSerializer.toJSON(contentField), ...(nonContentData as any)};
    }
  });

  const fakeAttachments = data.attachments.map(dataAttachmentToFakeInstanceAttachment);

  const fakeCardInstance = {
    ...data,
    status: data.isDoc ? "doc" : data.assignee ? "assigned" : "unassigned",
    attachments: fakeAttachments,
    assignee: data.assignee && api.getModel({modelName: "user", id: data.assignee}),
    milestone: data.milestoneId && api.getModel({modelName: "milestone", id: data.milestoneId}),
    sprint: data.sprintId && api.getModel({modelName: "sprint", id: data.sprintId}),
    $meta: {
      exists: () => false,
      get: (f: any) => data[f],
      isFieldLoaded: () => true,
      isCardCreationPlaceholder: true,
    },
    deck: data.deckId && api.getModel({modelName: "deck", id: data.deckId}),
    addAsBookmark: !data.deckId || data.asBookmark,
    creator: root.loggedInUser,
    coverFile:
      data.coverFileId && (fakeAttachments.find((a: any) => a.id === data.coverFileId) || {}).file,
    meta: {},
  };

  // const [version, setVersion] = useState(1);
  const meId = root.loggedInUser?.$meta.get("id", null);

  const handleCardChange = (arg: any) => {
    setNonContentData((existing: any) => {
      const next = typeof arg === "function" ? arg(existing) : arg;
      if ("assigneeId" in next) {
        next.assignee = next.assigneeId;
        delete next.assigneeId;
      }
      if ("assignee" in next) {
        if (existing.assignee && !next.assignee) {
          next.putInQueue = false;
        }
      }
      // if putInQueue is set, and no assignee is present, we should set it to me
      if ("putInQueue" in next && !("assingee" in next)) {
        if (!existing.putInQueue && next.putInQueue) {
          if (!existing.assignee) next.assignee = meId;
        }
      }
      return {...existing, ...next};
    });
  };

  const errors = useCardErrors({
    root,
    card: fakeCardInstance,
    putInQueue: data.putInQueue,
  });

  const handleSubmit = (sentData: any) => {
    if (errors.size) return null;
    const {
      assignee,
      content: _,
      id,
      deckId,
      coverFileId,
      presetId,
      subscribeCreator,
      putInQueue,
      dueDate,
      asBookmark: submittedAsBookmark,
      ...rest
    } = {...data, ...sentData} as any;
    const createData = {
      assigneeId: assignee,
      content: typeof sentData?.content === "string" ? sentData?.content : contentField.getText(),
      subscribeCreator,
      deckId,
      putInQueue,
      dueDate: dueDate && dayToDateStr(dueDate),
      addAsBookmark: submittedAsBookmark,
      ...rest,
      userId: root.loggedInUser.id,
      fakeCoverFileId: coverFileId,
    };
    return api.mutate.cards.create(createData).then((newCard: any) => {
      cardCreationPersistStore.clearStoredValue(arenaCtx);
      justSavedIdRef.current = id;
      if (createAnother) {
        if (showPreview) setShowPreview(false);
        if (presetId) {
          waitForResultPromise(
            () => api.getModel({modelName: "cardPreset", id: presetId})!.data
          ).then(({content: presetContent, ...presetRest}: any) => {
            handleCardChange({
              ...presetRest,
              id: `new-${(nextId += 1)}`,
              presetId,
              milestoneId: data.milestoneId,
              sprintId: data.sprintId,
              asBookmark: data.asBookmark,
              deckId: data.deckId,
              subscribeCreator: data.subscribeCreator,
              assignee,
              isDoc: false,
              attachments: [],
            });
            contentField.setText(presetContent || "");
            // setVersion((v) => v + 1);
          });
        } else {
          handleCardChange({
            id: `new-${(nextId += 1)}`,
            masterTags: data.masterTags,
            effort: data.effort,
            priority: data.priority,
            milestoneId: data.milestoneId,
            sprintId: data.sprintId,
            assignee,
            asBookmark: data.asBookmark,
            deckId: data.deckId,
            subscribeCreator: data.subscribeCreator,
            isDoc: data.isDoc,
            attachments: [],
          });
          contentField.setText("");
          // setVersion((v) => v + 1);
        }
        cardHighlightActions.highlightCardId(newCard.id, 2000);
      } else {
        goToCard({cardId: newCard.id});
        cardHighlightActions.highlightCardId(newCard.id, 2000);
        // add some time to allow the navigation to finish
        return new Promise((res) => setTimeout(res, 500));
      }
    });
  };

  const handlePresetChange = (presetId: string) => {
    if (presetId) {
      waitForResultPromise(() => api.getModel({modelName: "cardPreset", id: presetId})!.data).then(
        ({content: presetContent, ...rest}: any) => {
          handleCardChange({
            ...rest,
            isDoc: false,
            presetId,
          });
          contentField.setText(presetContent || "");
        }
      );
      setNonContentData((existing: any) => ({...existing, presetId}));
    } else {
      handleCardChange({
        masterTags: [],
        effort: null,
        priority: null,
        assignee: null,
        presetId: null,
      });

      contentField.setText("");
    }
  };

  const handleUpload = async (fileData: any) => {
    const attachment = {
      id: `${(nextFakeAttachmentId += 1)}-${new Date().getTime()}`,
      fileData,
      creatorId: root.loggedInUser.id,
      content: fileData.fileName,
    };
    setNonContentData((existing: any) => ({
      ...existing,
      attachments: [...existing.attachments, attachment],
    }));
    setAttBling("+1");
    const meta = await getImageDimensionsFromUrl(fileData.url);
    return {url: fileData.url, meta};
  };

  const handleAttachmentDelete = (attachmentId: string) => {
    setNonContentData((existing: any) => ({
      ...existing,
      attachments: existing.attachments.filter((att: any) => att.id !== attachmentId),
    }));
    setAttBling("-1");
  };

  const handleAttachmentEdit = ({id, content}: any) => {
    setNonContentData((prev: any) => {
      const attIdx = prev.attachments.findIndex((a: any) => a.id === id);
      if (attIdx >= 0) prev.attachments[attIdx] = {...prev.attachments[attIdx], content};
      return {...prev, attachments: prev.attachments};
    });
    return Promise.resolve(true);
  };

  const canSetCoverFile = () => {
    if (!data.parentCardId) return true;
    if (root.account.allowInheritHeroCover === false) return true;
    const parentCard = api.getModel({modelName: "card", id: data.parentCardId});
    return !Boolean(parentCard?.coverFile);
  };

  const handleSetCoverFile = canSetCoverFile()
    ? (coverFileId: string) => handleCardChange({coverFileId})
    : null;

  const {dropAreaProps, ongoingUploads, onReceiveFile} = useUpload({
    id: "attachment-new",
    affectsQuota: true,
    onUpload: handleUpload,
    multiple: true,
  });

  const getButtonLabel = () => {
    if (arenaCtx.type === "deck") return "Save Card to Deck";
    if (onMilestone) return `Add to Milestone`;
    if (onSprint) return `Add to Run`;
    if (arenaCtx.type === "handQueue" && !data.putInQueue) return "Save Card";

    return !data.assignee || meId === data.assignee ? "Save Card to Hand" : "Save to Owner's Hand";
  };

  const [focusEl, focusActive, setFocusActive] = useFocusMode({
    card: fakeCardInstance,
    root,
    onReceiveFile,
    canModify: true,
    location,
    history,
    uploadInfo: {ongoingUploads, dropAreaProps},
    editOnly: true,
    saveButtonLabel: getButtonLabel(),
    onSubmit: async (content) => handleSubmit({content}),
    getInitialContent: () =>
      lexicalSerializer.fromJSON(
        (cardCreationPersistStore.getStoredValue(arenaCtx) as any)?.content
      ) ||
      (deck?.$meta.get("defaultCard", {content: null}).content ?? ""),
    contentKey: getContentKey(arenaCtx),
    onCloseWithText: (text) => {
      contentField.setText(text);
    },
  });
  const focusProps: FocusProps = {active: focusActive, setActive: setFocusActive};

  const {Container, containerProps, sidePanelKey} = useUnifiedCardContainer({
    location,
    history,
    onClose,
    category,
    sidePanels: [
      {
        key: "props",
        content: (
          <PropEdit
            card={fakeCardInstance}
            onChange={handleCardChange}
            onClose={handleCloseSidePanel}
            root={root}
            isCreating
            presetId={data.presetId}
            onSetPresetId={handlePresetChange}
            createInQueue={inQueue}
          />
        ),
        button: {
          comp: PropPanelButton,
        },
      },
      {
        key: "attachments",
        content: (
          <Attachments
            card={fakeCardInstance}
            root={root}
            onClose={handleCloseSidePanel}
            onUpload={handleUpload}
            onDelete={handleAttachmentDelete}
            onEdit={handleAttachmentEdit}
            onSetCoverFile={handleSetCoverFile}
            isCreating
            canModify
            location={location}
            history={history}
          />
        ),
        button: {
          comp: AttachmentPanelButton,
          props: {
            tooltip: "Upload attachments",
            variant: data.attachments.length > 0 ? "nonEmpty" : "default",
            blingRef: attBlingRef,
          },
        },
      },
      {
        key: "childCards",
        content: (
          <WorkflowSidebarForCardCreation
            card={fakeCardInstance}
            root={root}
            onChange={handleCardChange}
          />
        ),
        button: {
          comp: ChildCardPanelButtonForCardCreation,
          props: {},
        },
      },
    ],
    sideBottom: <BottomButtons card={fakeCardInstance} onChange={handleCardChange} />,
  });

  const placeholder = useCreationHint({root});

  const handleDeckChange = (deckId: string) => {
    applyDeckChange({data, deckId, contentField, isEmpty}).then((updates) => {
      setNonContentData((existing: any) => ({
        ...existing,
        ...updates,
        ...(deckId === null && existing.assignee && existing.assignee !== root.loggedInUser.id
          ? {assignee: null}
          : {}),
        ...(deckId === null && {parentCardId: null}),
        deckId,
      }));
    });
  };

  usePropOnHoverChange({
    type: "BIG_CARD",
    updateFn: ({effort, priority}: any) => {
      handleCardChange(priority !== undefined ? {priority} : {effort});
    },
  });

  return (
    <Container
      contextName="CreateCard"
      {...containerProps}
      uploadArea={
        <Uploader.DropArea
          {...dropAreaProps}
          label="Drop attachments here"
          labelClassName={cardContainerStyles.stickyTop}
        />
      }
    >
      <FilePasteHandler onReceiveFile={onReceiveFile} />
      <CreateCardTopArea
        card={fakeCardInstance}
        root={root}
        arenaCtx={arenaCtx}
        onDeckChange={handleDeckChange}
        onChange={handleCardChange}
        category={category}
        inboxDeck={inboxDeck}
      />
      <CardDetailFrame
        card={fakeCardInstance}
        type="BIG_CARD"
        status={fakeCardInstance.status}
        root={root}
        onCardChange={handleCardChange}
        canModify
      >
        <Col minHeight="0" flex="auto" relative>
          {sidePanelKey !== "attachments" && (
            <ShowUploads
              ongoingUploads={ongoingUploads}
              className={cx(dsStyles.px["12px"], dsStyles.py["8px"])}
            />
          )}
          {showPreview ? (
            <PreviewCardContent
              contentAsText={contentField.getText()}
              togglePreview={togglePreview}
              handleSubmit={handleSubmit}
              buttonLabel={getButtonLabel()}
              project={fakeCardInstance.deck && fakeCardInstance.deck.project}
            />
          ) : (
            <CardContentForm
              card={fakeCardInstance}
              contentField={contentField}
              isEmpty={isEmpty}
              onReceiveFile={onReceiveFile}
              togglePreview={togglePreview}
              handleSubmit={handleSubmit}
              buttonLabel={getButtonLabel()}
              category={category}
              placeholder={placeholder}
              hasErrors={errors.size > 0}
              onCardChange={handleCardChange}
              focusProps={focusProps}
            />
          )}
          {errors.size > 0 && (
            <Col ml="auto" px="12px" py="8px">
              {[...errors].map((err, idx) => (
                <Text key={idx} type="label12" colorTheme="alert25" color="primary">
                  {err}
                </Text>
              ))}
            </Col>
          )}
          <AddAnotherItem
            card={fakeCardInstance}
            label="card"
            active={createAnother}
            onChange={() => setCreateAnother((v) => !v)}
          />
        </Col>
        <CardDetailFrameAboveBottom
          card={fakeCardInstance}
          project={fakeCardInstance.deck?.project}
          root={root}
          canModify
        />
      </CardDetailFrame>
      {focusEl}
    </Container>
  );
};

const Placeholder = () => {
  const {width} = useSidebarWidth();
  return (
    <RawCardContainer
      contextName="CreateCard"
      width={width}
      boxProps={{align: "center", justify: "center", width: "100%"}}
    >
      <DSSpinner size={24} />
    </RawCardContainer>
  );
};

type CreateCardProps = Omit<InnerCreateCardProps, "deck" | "initialSidePanel" | "inboxDeck">;
const CreateCard = (props: CreateCardProps) => {
  const {root} = props;
  const accountId = root.account.$meta.get("id", null);
  const {inboxDeck} =
    (accountId &&
      root.loggedInUser.$meta.find("accountSettings", {
        accountId,
      })[0]) ||
    {};
  const deck: Deck = props.arenaCtx.type === "deck" ? props.arenaCtx.model : inboxDeck;
  const deckLoaded = Boolean(
    !deck || (deck.$meta.get("defaultCard", null) && deck.$meta.get("allowedCardTypes", null))
  );

  return deckLoaded ? (
    <InnerCreateCard
      {...props}
      deck={deck}
      inboxDeck={inboxDeck ?? null}
      initialSidePanel="props"
    />
  ) : (
    <Placeholder />
  );
};

export default CreateCard;
